import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, ShieldCheck } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { OTP_LENGTH } from '@mybusinessmate/schema';
import { authClient } from '../../lib/api';
import { isReauthFresh, useReauthStore, verifyPassword } from '../../lib/reauth';
import { useSession } from '../../lib/session';
import { Button } from '../ui/button';
import { Dialog } from '../ui/dialog';
import { Input, PasswordInput } from '../ui/input';
import { OtpInput } from '../ui/otp-input';
import { textLinkClass } from '../ui/text-link';

export type ForgotPasswordPath = '/subdomain/admin/forgot-password' | '/subdomain/app/forgot-password';

export interface ReauthOptions {
  /** Minutes a confirmed password stays valid (the workspace's policy for staff). Defaults to 10. */
  minutes?: number;
  /** Where "Forgot password?" goes, which differs between the client app and the staff app. */
  forgotPasswordTo: ForgotPasswordPath;
}

/** What the prompt says when it is not the staff default: the vault asks per login, in the client's words (Figma C03.4a). */
export interface ReauthCopy {
  title: string;
  /** Replaces the "You're about to … We won't ask again for N minutes" paragraph. */
  description: string;
  /** The confirm button ("Show login"); defaults to "Continue". */
  confirmLabel?: string;
}

interface Pending {
  reason: string;
  copy?: ReauthCopy;
  /** Receives the password when it was just typed (not when the earlier confirmation is still fresh). */
  run: (password?: string) => void;
  /** Always ask, even inside the unlocked window (used when the API itself needs the password). */
  always: boolean;
}

function Body({
  reason,
  copy,
  minutes,
  twoFactorOn,
  forgotPasswordTo,
  onDone,
  onClose,
}: {
  reason: string;
  copy?: ReauthCopy;
  minutes: number;
  twoFactorOn: boolean;
  forgotPasswordTo: ForgotPasswordPath;
  onDone: (password?: string) => void;
  onClose: () => void;
}) {
  const markConfirmed = useReauthStore((s) => s.markConfirmed);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [useCode, setUseCode] = useState(false);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const confirmWithPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    const check = await verifyPassword(password);
    setBusy(false);
    if (!check.ok) {
      setError(check.message);
      return;
    }
    markConfirmed();
    onDone(password);
  };

  const confirmWithCode = async (value: string) => {
    setBusy(true);
    setError(undefined);
    const { error: authError } = await authClient.twoFactor.verifyTotp({ code: value });
    setBusy(false);
    const mapped = mapAuthError(authError);
    if (mapped) {
      setError(
        mapped.kind === 'invalid_code' ? 'That code didn’t work. Try the newest one in your app.' : mapped.message,
      );
      return;
    }
    markConfirmed();
    onDone();
  };

  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      <p className="text-body-medium text-text-secondary">
        {copy && !useCode ? (
          copy.description
        ) : (
          <>
            You’re about to {reason}. {useCode ? 'Enter the code from your authenticator app' : 'Enter your password'}{' '}
            to continue. We won’t ask again for {minutes} minutes.
          </>
        )}
      </p>
      {useCode ? (
        <div className="flex flex-col gap-(--mbm-space-3)">
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              setError(undefined);
            }}
            onComplete={(c) => void confirmWithCode(c)}
            disabled={busy}
            error={Boolean(error)}
            autoFocus
          />
          {error ? (
            <p role="alert" className="text-body-small text-feedback-error">
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <form id="reauth-form" onSubmit={confirmWithPassword} noValidate>
          {copy ? (
            // The client's prompt: the lock sits in the field and the password stays hidden (Figma C03.4a).
            <Input
              type="password"
              leadingIcon={Lock}
              label="Password"
              autoComplete="current-password"
              autoFocus
              value={password}
              error={error}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(undefined);
              }}
            />
          ) : (
            <PasswordInput
              variant="filled"
              label="Password"
              autoComplete="current-password"
              autoFocus
              value={password}
              error={error}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(undefined);
              }}
            />
          )}
        </form>
      )}
      <div className="flex items-center justify-between">
        {twoFactorOn ? (
          <button
            type="button"
            className={textLinkClass}
            onClick={() => {
              setUseCode((v) => !v);
              setError(undefined);
            }}
          >
            {useCode ? 'Use my password instead' : 'Use authenticator code instead'}
          </button>
        ) : (
          <span />
        )}
        {/* The reset pages are for signed-out visitors, so this signs out first, then starts the reset. */}
        <button
          type="button"
          className="rounded-(--mbm-radius-xs) text-label-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          onClick={async () => {
            await authClient.signOut();
            queryClient.clear();
            useReauthStore.getState().clear();
            await navigate({ to: forgotPasswordTo });
          }}
        >
          Forgot password?
        </button>
      </div>
      <div className="grid grid-cols-2 gap-(--mbm-space-3)">
        <Button variant="secondary" size="compact" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {useCode ? (
          <Button
            size="compact"
            loading={busy}
            disabled={code.length !== OTP_LENGTH}
            onClick={() => void confirmWithCode(code)}
          >
            {copy?.confirmLabel ?? 'Continue'}
          </Button>
        ) : (
          <Button size="compact" type="submit" form="reauth-form" loading={busy} disabled={!password}>
            {copy?.confirmLabel ?? 'Continue'}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Sensitive staff actions re-ask for the password, then stay unlocked for the workspace's window (10 minutes by
 * default, A16.7). `guard('view your backup codes', run)` runs `run` straight away inside that window, otherwise
 * after the "Confirm it's you" dialog (A16.2). Render the returned `dialog` once in the page.
 */
export function useReauth({ minutes = 10, forgotPasswordTo }: ReauthOptions): {
  guard: (reason: string, run: (password?: string) => void, options?: { always?: boolean; copy?: ReauthCopy }) => void;
  dialog: ReactNode;
} {
  const { data: session } = useSession();
  const [pending, setPending] = useState<Pending | null>(null);

  const guard = useCallback(
    (reason: string, run: (password?: string) => void, options?: { always?: boolean; copy?: ReauthCopy }) => {
      const always = options?.always ?? false;
      if (!always && isReauthFresh(useReauthStore.getState().confirmedAt, minutes)) run();
      else setPending({ reason, copy: options?.copy, run, always });
    },
    [minutes],
  );

  const dialog = (
    <Dialog
      open={pending !== null}
      onClose={() => setPending(null)}
      icon={ShieldCheck}
      title={pending?.copy?.title ?? 'Confirm it’s you'}
    >
      {pending ? (
        <Body
          reason={pending.reason}
          copy={pending.copy}
          minutes={minutes}
          forgotPasswordTo={forgotPasswordTo}
          twoFactorOn={Boolean(session?.user.twoFactorEnabled)}
          onClose={() => setPending(null)}
          onDone={(password) => {
            const { run } = pending;
            setPending(null);
            run(password);
          }}
        />
      ) : null}
    </Dialog>
  );

  return { guard, dialog };
}
